import React, { FC, useCallback, useMemo } from "react";
import { generatePath, useNavigate } from "react-router";
import { COLOR, View, Image } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import "@lookiero/sty-psp-ui";
import { ButtonIcon, Text } from "@lookiero/sty-psp-ui";
import { ProductPreview } from "../../../../../../domain/projections/ProductPreview";
import { ProductVariant } from "../../../../../../domain/projections/Wishlist";
import { Label } from "../../../../components/label/Label";
import { TouchableWithoutFeedback } from "../../../../components/touchableWithoutFeedback/TouchableWithoutFeedback";
import { useFormatCurrency } from "../../../../hooks/useFormatCurrency";
import { useStaticInfo } from "../../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { Routes } from "../../../../routing/routes";
import { cdnImageUrl } from "../../../../shared/util/cdnImageUrl";
import { financial } from "../../../../shared/util/financial/financial";
import { List } from "../../../../tracking/tracking";
import { useTrackItemSelected } from "../../../../tracking/useTrackItemSelected";
import { style as wishlistItemStyle } from "./WishlistItem.style";

const IMAGE_WIDTH = 216;

interface WishlistItemProps {
  readonly productVariant: ProductVariant;
  readonly available?: boolean;
  readonly onDelete: (variantId: string) => void;
  readonly onAddToBasket: (variantId: string) => void;
}

const WishlistItem: FC<WishlistItemProps> = ({ productVariant, onDelete, onAddToBasket, available }) => {
  const { variantId, imageUrl, title, brand, lastUnits, size, color, price } = productVariant;
  const {
    session: { user },
    basePath,
    tradename,
  } = useStaticInfo();
  const navigate = useNavigate();

  const colorText = useI18nMessage({
    domain: DOMAIN,
    id: `catalog.color.${color}`,
  });
  const labelLastItemsText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.LABEL_LAST_ITEMS,
  });
  const productStockOutModalTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_STOCK_OUT_MODAL_TITLE,
  });

  const style = useMemo(() => wishlistItemStyle(), []);

  const trackItemSelected = useTrackItemSelected({
    country: user.country,
    segment: user.segment,
    userId: user.id,
    tradename,
  });

  const isProductInSales = price.discountedPrice && price.discountedPrice.percentage > 0;
  const formatCurrency = useFormatCurrency();

  const handleOnPress = useCallback(() => {
    const product: ProductPreview = {
      id: variantId,
      title: productVariant.title,
      brand: productVariant.brand,
      price: productVariant.price,
    } as ProductPreview;

    trackItemSelected({
      list: List.WISHLIST,
      productPreview: product,
    });

    navigate(generatePath(`${basePath}/${Routes.PRODUCT_DETAIL}`, { variantId }), {
      state: { innerNavigation: true },
    });
  }, [
    variantId,
    productVariant.title,
    productVariant.brand,
    productVariant.price,
    trackItemSelected,
    navigate,
    basePath,
  ]);

  return (
    <View style={style.wishlistItem}>
      <TouchableWithoutFeedback onPress={handleOnPress}>
        <Image
          alt={title}
          resizeMode="cover"
          src={cdnImageUrl({ imageUrl, width: IMAGE_WIDTH })}
          style={[style.wishlistItemImage, !available ? style.unavailableImage : undefined]}
          testID={`wishlist-item-image-${productVariant.variantId}`}
        />
      </TouchableWithoutFeedback>
      <View style={style.wishlistItemBody}>
        <View style={style.wishlistItemInfo}>
          <View>
            <Text level={2} style={available ? style.brand : style.brandUnavailable} variant="detail">
              {brand}
            </Text>
            <Text level={2} style={available ? style.title : style.titleUnavailable} variant="detail">
              {title}
            </Text>
            <Text level={2} style={available ? style.variant : style.variantUnavailable} variant="detail">
              {size} / {colorText}
            </Text>
          </View>
          {lastUnits && (
            <View style={style.labelWrapper}>
              <Label color={COLOR.BG_WARNING}>{labelLastItemsText}</Label>
            </View>
          )}
          {!available ? (
            <View style={style.labelWrapper}>
              <Label color={COLOR.BG_WARNING}>{productStockOutModalTitleText}</Label>
            </View>
          ) : undefined}
          <View>
            {!isProductInSales ? (
              <Text level={2} style={available ? style.price : style.priceUnavailable} variant="detail">
                {formatCurrency({ amount: financial(price.amount) })}
              </Text>
            ) : (
              <View style={style.priceWrapper}>
                <Text level={2} style={available ? style.price : style.priceUnavailable} variant="detail">
                  {price.discountedPrice && formatCurrency({ amount: financial(price.discountedPrice?.amount) })}
                </Text>
                <Text
                  level={2}
                  style={available ? style.priceStrikethrough : style.priceStrikethroughUnavailable}
                  variant="detail"
                >
                  {formatCurrency({ amount: financial(price.amount) })}
                </Text>
              </View>
            )}
          </View>
        </View>
        <View style={style.wishlistItemButtons}>
          <ButtonIcon
            name="basket"
            style={{ button: style.wishlistItemButton, icon: !available && style.disabledIcon }}
            testID={`wishlist-item-add-to-basket-${productVariant.variantId}`}
            onPress={() => onAddToBasket(variantId)}
          />
          <ButtonIcon
            name="trash"
            style={{ button: style.wishlistItemButton }}
            testID={`wishlist-delete-action-${productVariant.variantId}`}
            onPress={() => onDelete(variantId)}
          />
        </View>
      </View>
    </View>
  );
};

export { WishlistItem };
